import React from "react";
import { Box, Checkbox, FormControlLabel, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { NodeSelect, NodeTextField, NODE_UI } from "../../NodeUI";
import { DURATION_UNITS, durationToIso, parseDuration } from "./planTemplateModel";

const StepCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: NODE_UI.spacing.md,
  padding: NODE_UI.spacing.md,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa",
}));

const StepHeader = styled(Stack)(() => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: NODE_UI.spacing.sm,
}));

const StepNumber = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const DurationRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(90px, 120px) minmax(120px, 1fr)",
  gap: NODE_UI.spacing.sm,
  alignItems: "end",
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
}));

const DurationField = ({ label, value, fallbackUnit, onChange }) => {
  const parsed = parseDuration(value, fallbackUnit);
  return (
    <DurationRow>
      <NodeTextField
        label={label}
        value={parsed.amount}
        type="number"
        inputProps={{ min: 1 }}
        onChange={(next) => onChange(durationToIso(next, parsed.unit))}
      />
      <NodeSelect
        label="Unit"
        value={parsed.unit}
        options={DURATION_UNITS}
        onChange={(nextUnit) => onChange(durationToIso(parsed.amount, nextUnit))}
      />
    </DurationRow>
  );
};

const PlanStepCard = ({ step, index, onChange, onRemove, canRemove }) => {
  const patch = (changes) => onChange({ ...step, ...changes });

  return (
    <StepCard>
      <StepHeader>
        <StepNumber>Step {index + 1}</StepNumber>
        <Tooltip title="Remove step">
          <span>
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={!canRemove}
              sx={{ color: "text.secondary" }}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </StepHeader>

      <NodeTextField
        label="Title"
        fullWidth
        value={step.title}
        onChange={(next) => patch({ title: next })}
        placeholder="e.g. Ensure the workflow completes within 24 hours"
      />

      <NodeTextField
        label="Description"
        fullWidth
        multiline
        minRows={2}
        value={step.description}
        onChange={(next) => patch({ description: next })}
      />

      <NodeTextField
        label="Acceptance criteria (one per line)"
        fullWidth
        multiline
        minRows={2}
        value={(step.acceptanceCriteria || []).join("\n")}
        onChange={(next) =>
          patch({ acceptanceCriteria: next.split("\n") })
        }
        helperText="How the operator knows this step is met."
      />

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(step.timeBound)}
            onChange={(event) => patch({ timeBound: event.target.checked })}
          />
        }
        label="Time-bound (schedule wakes for this step)"
        sx={{
          m: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: NODE_UI.fontSize.value,
            color: "text.primary",
          },
        }}
      />

      {step.timeBound ? (
        <>
          <DurationField
            label="Deadline"
            value={step.deadline}
            fallbackUnit="hours"
            onChange={(next) => patch({ deadline: next })}
          />
          <DurationField
            label="Check every"
            value={step.checkInterval}
            fallbackUnit="hours"
            onChange={(next) => patch({ checkInterval: next })}
          />
          <NodeTextField
            label="If met"
            fullWidth
            multiline
            minRows={2}
            value={step.ifMet}
            onChange={(next) => patch({ ifMet: next })}
            placeholder="What the operator should do when the criteria are met."
          />
          <NodeTextField
            label="If not met by the deadline"
            fullWidth
            multiline
            minRows={2}
            value={step.ifNotMet}
            onChange={(next) => patch({ ifNotMet: next })}
            placeholder="e.g. Use send_email to notify the owner."
          />
        </>
      ) : null}
    </StepCard>
  );
};

export default PlanStepCard;
